import { getCrmPointsRules } from '@/api/public';
import { CrmPointRule, IProductDetail } from '@/api/types/public';
import Dialog from '@/components/Dialog';
import Tooltip from '@/components/Tooltip';
import { useStore } from '@/providers/app-store-provider';
import { useSpmDomAttr } from '@/reporting/spm';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accDiv, accMul } from '@/utils/tools';
import { Button } from '@mui/material';
import { QuestionOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useProductDetailSpmCodes,
  resolveProductDetailsBtnSpmCnt,
} from '../hooks/useGoodsDetailTrack';
import {
  buildCouponSectionLabels,
  formatSecondHalfPriceContent,
  PublicHalfPriceConfig,
  shouldHideCouponsText,
} from './CouponSection.utils';
import PointsRule from './dialog/PointsRule';

interface CouponSectionProps {
  showCouponTag: boolean;
  couponTips?: string;
  product: IProductDetail;
  onShowCoupons: () => void;
  isWeb?: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const CouponSection: React.FC<CouponSectionProps> = ({
  showCouponTag,
  couponTips,
  product,
  onShowCoupons,
  isWeb = false,
  SDProductDetailsPageBtnClick,
}) => {
  const [showPointsDialog, setShowPointsDialog] = useState<boolean>(false);
  const [showSecondHalfPriceDialog, setShowSecondHalfPriceDialog] = useState<boolean>(false);
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：终端编码表与点击上报（useGoodsDetailTrack）
  // 同源同判断；右侧优惠券入口为 open_coupons，前方第二副说明为 open_second_frame_offer。
  // 首副标签目前只有文案，无说明弹框，不挂 open_first_frame_offer。
  const spmCodes = useProductDetailSpmCodes(isWeb);
  const secondFrameDomSpm = useSpmDomAttr(
    SDProductDetailsPageBtnClick
      ? resolveProductDetailsBtnSpmCnt(spmCodes, '50% OFF on 2nd Frame')
      : undefined,
  );
  const couponsDomSpm = useSpmDomAttr(
    SDProductDetailsPageBtnClick ? resolveProductDetailsBtnSpmCnt(spmCodes, 'Coupons') : undefined,
  );
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const [crmPointRule, setCrmPointRule] = useState<CrmPointRule>({});
  const waiMode = useStore((state) => state.waiMode);
  const currencyList = useStore((state) => state.currencyList);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const t = useTranslations('goodsDetail.coupon');
  const publicHalfPrice: PublicHalfPriceConfig = useMemo(
    () =>
      (
        publicResourceData?.config_names as
          | { product_content_half_price_member?: PublicHalfPriceConfig }
          | undefined
      )?.product_content_half_price_member || {},
    [publicResourceData?.config_names],
  );

  const defaultCurrency = useMemo(() => {
    const defaultCurrencyName = env.NEXT_PUBLIC_DEFAULT_CURRENCY || 'USD';
    return (
      currencyList.find((item) => item.name === defaultCurrencyName) ||
      currencyList.find((item) => item.basics === 1) ||
      currencyList[0] || { name: defaultCurrencyName }
    );
  }, [currencyList]);

  // 获取基础价格信息
  const basePriceInfo = useMemo(() => {
    // 会员价
    if (
      userInfo.is_member === 1 &&
      product &&
      product[`price_info_v${userInfo.member_level}`] &&
      product[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      const priceInfo = product[`price_info_v${userInfo.member_level}`].filter((item) => {
        return item.currency === env.NEXT_PUBLIC_DEFAULT_CURRENCY;
      });
      return { price: priceInfo[0].price, sell_price: priceInfo[0].sell_price };
    }
    const priceInfo = product.price_info.filter((item) => {
      return item.currency === env.NEXT_PUBLIC_DEFAULT_CURRENCY;
    });
    return { price: priceInfo[0].price, sell_price: priceInfo[0].sell_price };
  }, [product, userInfo]);

  // 计算积分
  const points = useMemo(() => {
    try {
      if (!crmPointRule.obtain || !clientTokenReady) return 0;
      const { is_member, member_level } = userInfo;
      let multiple_field = 'order_point_multiple'; // 游客
      if (token) {
        // is_member 是否是会员 1是 2否
        multiple_field =
          is_member === 2 ? 'order_point_multiple' : `order_point_multiple_v${member_level}`; // 动态的字段处理
      }
      const multiple = crmPointRule?.obtain?.[multiple_field];
      // 计算积分获取比例
      /* 举例：10美元    4积分 ，计算，1美元多少积分  10/4  */
      const proportion = accDiv(crmPointRule.obtain.ratio, 10);
      // 积分是按照，basePriceInfo 进行处理
      const _points = accMul(basePriceInfo.sell_price, proportion);
      const multiple_points = accMul(_points, multiple);
      return Math.floor(multiple_points);
    } catch (e) {
      logger.log(e);
      return 0;
    }
  }, [userInfo, token, clientTokenReady, crmPointRule, basePriceInfo]);

  useEffect(() => {
    getCrmPointsRules().then(({ code, data }) => {
      if (code === 200) {
        setCrmPointRule(data);
      }
    });
  }, []);

  const ratioNum = useMemo(() => {
    try {
      return Number(crmPointRule?.deduct?.ratio);
    } catch (e) {
      logger.log(e);
      return 0;
    }
  }, [crmPointRule]);

  const renderPointsRule = useCallback(() => {
    return (
      <div>
        <p className={`text-[14px] ${isWeb ? '' : 'mb-10'}`}>
          {t('pointEqualPrice', {
            points: ratioNum,
            pointsText: ratioNum > 1 ? t('points') : t('point'),
            price: formatPriceNumberWithSymbol('1', defaultCurrency, false),
          })}
        </p>
        <p className="text-[14px]">{t('youCanRedeemThePointsOnYourNextPurchase')}</p>
      </div>
    );
  }, [defaultCurrency, ratioNum, isWeb, t]);

  const secondHalfPriceContent = useMemo(
    () => formatSecondHalfPriceContent(publicHalfPrice, defaultCurrency).replace(/\n/g, '<br/>'),
    [defaultCurrency, publicHalfPrice],
  );

  const secondHalfPriceContentNode = useMemo(
    () => (
      <div
        className="text-[14px] break-words"
        dangerouslySetInnerHTML={{
          __html: secondHalfPriceContent,
        }}
      />
    ),
    [secondHalfPriceContent],
  );

  const showSecondHalfPrice =
    Number(product?.is_second_half_price) === 1 && publicHalfPrice?.rule?.rule_flag === 1;

  const couponSectionLabels = useMemo(
    () =>
      buildCouponSectionLabels({
        couponTips,
        points,
        showSecondHalfPrice,
        publicHalfPrice,
        t,
      }),
    [couponTips, points, publicHalfPrice, showSecondHalfPrice, t],
  );
  const hideCouponsText = !isWeb && shouldHideCouponsText(couponSectionLabels);

  // 渲染商品详情标签模块
  const renderCommodityLabels = useCallback(() => {
    return couponSectionLabels.map((item, index: number) => {
      switch (item.type) {
        case 'coupon':
          // 优惠券模块
          return (
            <section
              key={`coupon-${index}`}
              className="h-[20px] px-[8px] rounded-[4px] bg-[#eeebff] text-[#051431] text-[11px] mx-[3px] my-[1px] flex shrink-0 items-center whitespace-nowrap"
              style={{
                fontSize: isWeb ? 'max(.625vw, 11px)' : '',
              }}
              onClick={() => {
                SDProductDetailsPageBtnClick?.('Coupons Label');
              }}
            >
              {item.text}
            </section>
          );

        case 'secondHalfPrice':
          // 第二件半价模块
          return (
            <section
              key={`second-half-price-${index}`}
              onClick={() => {
                if (!isWeb) {
                  setShowSecondHalfPriceDialog(true);
                }
                SDProductDetailsPageBtnClick?.('50% OFF on 2nd Frame');
              }}
              style={{
                fontSize: isWeb ? 'max(.625vw, 11px)' : '',
              }}
              {...secondFrameDomSpm}
              className="h-[20px] px-[8px] rounded-[4px] bg-[#eeebff] text-[#051431] text-[11px] cursor-pointer flex shrink-0 items-center mx-[3px] my-[1px] whitespace-nowrap"
            >
              {isWeb && (
                <Tooltip
                  variant="light"
                  placement="top"
                  title={secondHalfPriceContentNode}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: '290px',
                    },
                  }}
                >
                  <span className="flex items-center whitespace-nowrap">
                    {item.text}
                    <QuestionOutlined className="ml-4 text-[11px] text-[#051314]" />
                  </span>
                </Tooltip>
              )}
              {!isWeb && (
                <>
                  {item.text}
                  <QuestionOutlined className="ml-4 text-[11px] text-[#a9a9a9] " />
                </>
              )}
            </section>
          );

        case 'point':
          // 积分模块
          return (
            <section
              key={`point-${index}`}
              onClick={() => {
                if (!isWeb) {
                  setShowPointsDialog(true);
                }
                SDProductDetailsPageBtnClick?.('Points');
              }}
              style={{
                fontSize: isWeb ? 'max(.625vw, 11px)' : '',
              }}
              className="h-[20px] px-[8px] rounded-[4px] bg-[#eeebff] text-[#051431] text-[11px] cursor-pointer flex shrink-0 items-center mx-[3px] my-[1px] whitespace-nowrap"
            >
              {isWeb && (
                <Tooltip
                  variant="light"
                  placement="top"
                  title={renderPointsRule()}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: '290px',
                    },
                  }}
                >
                  <span className="flex items-center whitespace-nowrap">
                    {t('earnPoints', {
                      points,
                      pointsText: points > 1 ? t('points') : t('point'),
                    })}
                    <QuestionOutlined className="ml-4 text-[11px] text-[#051314]" />
                  </span>
                </Tooltip>
              )}
              {!isWeb && (
                <>
                  {t('earnPoints', {
                    points,
                    pointsText: points > 1 ? t('points') : t('point'),
                  })}
                  <QuestionOutlined className="ml-4 text-[11px] text-[#a9a9a9] " />
                </>
              )}
            </section>
          );

        default:
          return null;
      }
    });
  }, [
    couponSectionLabels,
    isWeb,
    points,
    renderPointsRule,
    secondHalfPriceContentNode,
    secondFrameDomSpm,
    SDProductDetailsPageBtnClick,
    t,
  ]);

  if (!showCouponTag && couponSectionLabels.length === 0) return null;
  return (
    <div
      className={`coupon-section flex justify-between items-center ${
        isWeb ? 'mt-12' : 'pb-[6px] mt-[5px]'
      }`}
    >
      <div
        className={`coupon-labels flex ml-[-3px] ${
          isWeb ? 'w-[380px] flex-wrap' : 'min-w-0 flex-1 flex-wrap'
        }`}
      >
        {renderCommodityLabels()}
      </div>
      {couponTips && (
        <Button
          size="small"
          onClick={() => {
            SDProductDetailsPageBtnClick?.('Coupons');
            onShowCoupons();
          }}
          className="coupon-action normal-case text-[12px]"
          aria-label={t('coupons')}
          {...couponsDomSpm}
          endIcon={<RightArrowOutlined className={`${isWeb ? '!text-[12px]' : ''}`} />}
          sx={
            isWeb
              ? {
                  backgroundColor: colors.primaryPurple,
                  color: colors.primaryWhite,
                  fontSize: '12px',
                  height: '20px',
                  padding: '0px 5px',
                  '& .MuiButton-endIcon': {
                    color: colors.primaryWhite,
                    fontSize: '10px',
                    marginLeft: '2px',
                  },
                }
              : {
                  flex: '0 1 auto',
                  minWidth: '14px',
                  overflow: 'hidden',
                  padding: '0px',
                  color: waiMode ? colors.primaryBlack : '#808080',
                  fontSize: '3.2vw',
                  whiteSpace: 'nowrap',
                  '& .MuiButton-endIcon': {
                    flexShrink: 0,
                    marginLeft: hideCouponsText ? '0px' : '4px',
                    fontSize: '14px',
                    color: '#808080',
                  },
                }
          }
        >
          {!hideCouponsText && <span className="coupon-action-text">{t('coupons')}</span>}
        </Button>
      )}
      <PointsRule
        show={showPointsDialog}
        onClose={() => setShowPointsDialog(false)}
        renderPointsRule={renderPointsRule}
      />
      <Dialog.Confirm
        open={showSecondHalfPriceDialog}
        onClose={() => setShowSecondHalfPriceDialog(false)}
        okText={t('ok')}
        onOk={() => setShowSecondHalfPriceDialog(false)}
      >
        {secondHalfPriceContentNode}
      </Dialog.Confirm>
    </div>
  );
};

export default CouponSection;
